export type UnitMeasure =
  | "length"
  | "area"
  | "mass"
  | "volume"
  | "volumeFlowRate"
  | "temperature"
  | "time"
  | "frequency"
  | "speed"
  | "pace"
  | "pressure"
  | "digital"
  | "illuminance"
  | "partsPer"
  | "voltage"
  | "current"
  | "power"
  | "apparentPower"
  | "reactivePower"
  | "energy"
  | "reactiveEnergy"
  | "angle";

interface MeasureMeta {
  label: string;
  description: string;
  shortLabel: string;
}

interface DefaultPair {
  amount: string;
  from: string;
  to: string;
}

interface PresetPair {
  label: string;
  amount: string;
  from: string;
  to: string;
}

export const measureOrder: UnitMeasure[] = [
  "length",
  "area",
  "mass",
  "volume",
  "temperature",
  "time",
  "speed",
  "pressure",
  "digital",
  "energy",
  "frequency",
  "angle",
  "pace",
  "volumeFlowRate",
  "illuminance",
  "partsPer",
  "voltage",
  "current",
  "power",
  "apparentPower",
  "reactivePower",
  "reactiveEnergy",
];

export const measureMeta: Record<UnitMeasure, MeasureMeta> = {
  length: {
    label: "Length",
    shortLabel: "Length",
    description: "Distance, height, travel and construction measurements.",
  },
  area: {
    label: "Area",
    shortLabel: "Area",
    description: "Land, floor plans, maps and surface coverage.",
  },
  mass: {
    label: "Mass",
    shortLabel: "Mass",
    description: "Weight and mass conversions from micrograms to tons.",
  },
  volume: {
    label: "Volume",
    shortLabel: "Volume",
    description: "Kitchen, liquid, storage and cubic volume conversions.",
  },
  volumeFlowRate: {
    label: "Flow Rate",
    shortLabel: "Flow",
    description: "Liquid and gas flow rates for home and engineering use.",
  },
  temperature: {
    label: "Temperature",
    shortLabel: "Temp",
    description: "Celsius, Fahrenheit, Kelvin and Rankine conversions.",
  },
  time: {
    label: "Time",
    shortLabel: "Time",
    description: "Durations from nanoseconds to years.",
  },
  frequency: {
    label: "Frequency",
    shortLabel: "Freq",
    description: "Rotational, audio, radio and waveform frequencies.",
  },
  speed: {
    label: "Speed",
    shortLabel: "Speed",
    description: "Transport, sport and motion speed conversions.",
  },
  pace: {
    label: "Pace",
    shortLabel: "Pace",
    description: "Running, walking and race pace conversions.",
  },
  pressure: {
    label: "Pressure",
    shortLabel: "Pressure",
    description: "Weather, tire, fluid and industrial pressure units.",
  },
  digital: {
    label: "Digital Storage",
    shortLabel: "Digital",
    description: "Bits, bytes and storage sizes for files and bandwidth.",
  },
  illuminance: {
    label: "Illuminance",
    shortLabel: "Light",
    description: "Lux and foot-candle light level conversions.",
  },
  partsPer: {
    label: "Parts Per",
    shortLabel: "PPM",
    description: "Concentration units such as ppm, ppb and beyond.",
  },
  voltage: {
    label: "Voltage",
    shortLabel: "Voltage",
    description: "Electrical potential from millivolts to kilovolts.",
  },
  current: {
    label: "Current",
    shortLabel: "Current",
    description: "Electrical current conversions from mA to kA.",
  },
  power: {
    label: "Power",
    shortLabel: "Power",
    description: "Real electrical and mechanical power conversions.",
  },
  apparentPower: {
    label: "Apparent Power",
    shortLabel: "VA",
    description: "Apparent power units such as VA and kVA.",
  },
  reactivePower: {
    label: "Reactive Power",
    shortLabel: "VAR",
    description: "Reactive power conversions for AC systems.",
  },
  energy: {
    label: "Energy",
    shortLabel: "Energy",
    description: "Energy units from joules to gigawatt-hours.",
  },
  reactiveEnergy: {
    label: "Reactive Energy",
    shortLabel: "VARh",
    description: "Reactive energy units for electrical analysis.",
  },
  angle: {
    label: "Angle",
    shortLabel: "Angle",
    description: "Angles for geometry, navigation and engineering.",
  },
};

export const defaultPairs: Record<UnitMeasure, DefaultPair> = {
  length: { amount: "1", from: "m", to: "ft" },
  area: { amount: "1", from: "m2", to: "ft2" },
  mass: { amount: "1", from: "kg", to: "lb" },
  volume: { amount: "1", from: "l", to: "gal" },
  volumeFlowRate: { amount: "1", from: "l/min", to: "gal/min" },
  temperature: { amount: "25", from: "C", to: "F" },
  time: { amount: "1", from: "h", to: "min" },
  frequency: { amount: "60", from: "Hz", to: "rpm" },
  speed: { amount: "100", from: "km/h", to: "m/s" },
  pace: { amount: "5", from: "min/km", to: "min/mi" },
  pressure: { amount: "1", from: "bar", to: "psi" },
  digital: { amount: "1", from: "GB", to: "MB" },
  illuminance: { amount: "500", from: "lx", to: "ft-cd" },
  partsPer: { amount: "1", from: "ppm", to: "ppb" },
  voltage: { amount: "230", from: "V", to: "kV" },
  current: { amount: "1", from: "A", to: "mA" },
  power: { amount: "1", from: "kW", to: "W" },
  apparentPower: { amount: "1", from: "kVA", to: "VA" },
  reactivePower: { amount: "1", from: "kVAR", to: "VAR" },
  energy: { amount: "1", from: "kWh", to: "J" },
  reactiveEnergy: { amount: "1", from: "kVARh", to: "VARh" },
  angle: { amount: "180", from: "deg", to: "rad" },
};

export const presetPairs: Partial<Record<UnitMeasure, PresetPair[]>> = {
  length: [
    { label: "Meters → Feet", amount: "1", from: "m", to: "ft" },
    { label: "Kilometers → Miles", amount: "5", from: "km", to: "mi" },
    { label: "Inches → Centimeters", amount: "12", from: "in", to: "cm" },
  ],
  area: [
    {
      label: "Square meters → Square feet",
      amount: "100",
      from: "m2",
      to: "ft2",
    },
    { label: "Acres → Hectares", amount: "1", from: "ac", to: "ha" },
  ],
  mass: [
    { label: "Kilograms → Pounds", amount: "70", from: "kg", to: "lb" },
    { label: "Ounces → Grams", amount: "16", from: "oz", to: "g" },
  ],
  volume: [
    { label: "Liters → Gallons", amount: "2", from: "l", to: "gal" },
    { label: "Cups → Milliliters", amount: "1", from: "cup", to: "ml" },
  ],
  temperature: [
    { label: "Celsius → Fahrenheit", amount: "25", from: "C", to: "F" },
    { label: "Fahrenheit → Celsius", amount: "72", from: "F", to: "C" },
  ],
  speed: [
    { label: "km/h → mph", amount: "100", from: "km/h", to: "m/h" },
    { label: "m/s → ft/s", amount: "10", from: "m/s", to: "ft/s" },
  ],
  digital: [
    { label: "GB → MB", amount: "1", from: "GB", to: "MB" },
    { label: "MB → Kb", amount: "10", from: "MB", to: "Kb" },
  ],
  pressure: [
    { label: "Bar → PSI", amount: "2.2", from: "bar", to: "psi" },
    { label: "kPa → Torr", amount: "101.325", from: "kPa", to: "torr" },
  ],
  energy: [
    { label: "kWh → Joules", amount: "1", from: "kWh", to: "J" },
    { label: "Joules → kJ", amount: "5000", from: "J", to: "kJ" },
  ],
  time: [
    { label: "Hours → Minutes", amount: "2", from: "h", to: "min" },
    { label: "Weeks → Days", amount: "3", from: "week", to: "d" },
  ],
};
